// src/modules/bookLocation/keyHandover/common/url/subscriptionQuerySchema.ts

import { BookingQuery } from "./bookingQuerySchema";

/**
 * Subscription Flow Query Schema
 *
 * Persists subscription flow state across browser refresh and navigation.
 * Merged with BookingQuery to maintain all state in URL.
 */
export interface SubscriptionQuery {
    /** Location ID (UUID or slug) - also stored in subscription for self-contained state */
    locationId?: string;

    /** Flow type: "new" or "existing" */
    subscriptionFlow?: "new" | "existing";

    /** Selected payment model: "subscription-new", "subscription-existing", or "single-booking" */
    paymentModel?: "subscription-new" | "subscription-existing" | "single-booking";

    // Flow 2 & 3: Email verification
    /** User email (non-sensitive for URL - used for verification) */
    email?: string;

    /** Email verification status */
    emailVerified?: "true" | "false";

    // Flow 2: New subscription
    /** Selected subscription plan ID from backend (commercial model ID) */
    selectedSubscriptionPlanId?: string;

    /** Eligibility check passed */
    eligible?: "true" | "false";

    /** Subscription payment completed */
    subscriptionPaid?: "true" | "false";

    /** Created subscription ID (after payment) */
    subscriptionId?: string;

    // Flow 2: Host data fields (non-sensitive)
    /** Host first name */
    hostFirstName?: string;

    /** Host last name */
    hostLastName?: string;

    /** Host email */
    hostEmail?: string;

    /** Host phone */
    hostPhone?: string;

    // Flow 3: Existing subscription
    /** Existing subscription verified */
    subscriptionVerified?: "true" | "false";
}

/**
 * Combined query schema for entire key handover flow
 */
export interface KeyHandoverQuery extends BookingQuery, SubscriptionQuery {}

/**
 * Canonical ordering for subscription query keys
 */
export const SUBSCRIPTION_QUERY_KEYS: readonly (keyof SubscriptionQuery)[] = [
    "locationId",
    "paymentModel",
    "subscriptionFlow",
    "email",
    "emailVerified",
    "selectedSubscriptionPlanId",
    "eligible",
    "subscriptionPaid",
    "subscriptionId",
    "hostFirstName",
    "hostLastName",
    "hostEmail",
    "hostPhone",
    "subscriptionVerified",
];

/**
 * Validation functions
 */
export function isValidSubscriptionFlow(value: string): boolean {
    return value === "new" || value === "existing";
}

export function isValidPaymentModel(value: string): boolean {
    return value === "subscription-new" || value === "subscription-existing" || value === "single-booking";
}

export function isValidEmail(value: string): boolean {
    // Basic email validation for URL safety (non-exhaustive)
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value);
}

export function isValidBoolean(value: string): boolean {
    return value === "true" || value === "false";
}
